import { useCallback, useState } from 'react';

/**
 * Per-user expand/collapse state for element groups, persisted in localStorage,
 * keyed by `${timelineId}:${groupId}`. Groups default to expanded.
 */
export function useExpandedGroups(timelineId: string | null | undefined) {
  const storageKey = `backtime:expandedGroups:${timelineId ?? 'local'}`;

  const [collapsed, setCollapsed] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      return new Set<string>(raw ? JSON.parse(raw) : []);
    } catch {
      return new Set<string>();
    }
  });

  const persist = useCallback(
    (next: Set<string>) => {
      try {
        localStorage.setItem(storageKey, JSON.stringify([...next]));
      } catch {
        /* ignore quota / private-mode errors */
      }
    },
    [storageKey]
  );

  const isExpanded = useCallback((groupId: string) => !collapsed.has(groupId), [collapsed]);

  const toggle = useCallback(
    (groupId: string) => {
      setCollapsed((prev) => {
        const next = new Set(prev);
        if (next.has(groupId)) next.delete(groupId);
        else next.add(groupId);
        persist(next);
        return next;
      });
    },
    [persist]
  );

  return { isExpanded, toggle };
}
